import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { validationRules } from '../../utils/validation';
import { handleFormErrors } from '../../utils/errorHandler';
import { useAuth } from '../../contexts/AuthContext';

const EmailVerification = () => {
  const { token: paramToken } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { resendVerificationEmail } = useAuth();
  const [verificationStatus, setVerificationStatus] = useState(null); // null, 'verifying', 'success', 'error'
  const [isResending, setIsResending] = useState(false);
  const [email, setEmail] = useState('');

  // Get token from either URL params or query string
  const queryParams = new URLSearchParams(location.search);
  const queryToken = queryParams.get('token');
  const token = paramToken || queryToken;

  // This effect runs when the component mounts or when the token changes
  useEffect(() => {
    // Clear any existing toasts when component mounts or token changes
    toast.dismiss();
    
    // Reset verification status when token changes
    setVerificationStatus(null);
    
    // Add a small delay before starting verification to ensure clean state
    const timer = setTimeout(() => {
      if (token) {
        setVerificationStatus('verifying');
        verifyEmail(token);
      } else {
        setVerificationStatus('error');
      }
    }, 100);
    
    return () => clearTimeout(timer);
  }, [token]);

  // Try both verification endpoints to ensure compatibility
  const verifyEmail = async (verificationToken) => {
    // Clear any existing toasts before starting verification
    toast.dismiss();
    
    try {

      
      // First try the query parameter endpoint
      let response;
      try {
        response = await axios.get(
          `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/auth/verify-email`,
          { params: { token: verificationToken } }
        );
      } catch (queryError) {
        // If query parameter endpoint fails, try route parameter endpoint
        response = await axios.get(
          `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/auth/verify-email/${verificationToken}`
        );
      }

      // Set state based on response
      if (response.data.success) {
        setVerificationStatus('success');
        // Delay toast to ensure state is updated first
        setTimeout(() => {
          toast.dismiss();
          toast.success('Email verified successfully!');
        }, 500);
      } else {
        setVerificationStatus('error');
        setTimeout(() => {
          toast.dismiss();
          toast.error(response.data.message || 'Verification failed');
        }, 500);
      }
    } catch (error) {
      console.error('Verification error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      setVerificationStatus('error');
      setTimeout(() => {
        toast.dismiss();
        toast.error(error.response?.data?.message || 'Verification failed');
      }, 500);
    }
  };

  const resendVerification = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    setIsResending(true);
    try {
      // Use the new validation and error handling utilities
      if (!validationRules.email.pattern.value.test(email)) {
        toast.error(validationRules.email.pattern.message);
        return;
      }
      
      const result = await resendVerificationEmail(email);
      
      if (!result.success) {
        // Use handleFormErrors if the response contains field-specific errors
        if (result.errors) {
          handleFormErrors(result.errors);
        } else {
          toast.error(result.error || 'Failed to resend verification email');
        }
      }
    } catch (error) {
      console.error('Resend verification error:', error);
      toast.error('An unexpected error occurred. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: 'easeOut'
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="max-w-md w-full space-y-8"
      >
        {/* Header */}
        <motion.div variants={itemVariants} className="text-center">
          <Link to="/" className="inline-flex items-center space-x-2 mb-6">
            <div className="w-12 h-12 bg-gradient-primary rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-xl">P</span>
            </div>
            <span className="text-2xl font-bold gradient-text">Prime Edge</span>
          </Link>
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
            Email Verification
          </h2>
          <p className="mt-2 text-gray-600 dark:text-gray-300">
            {verificationStatus === 'verifying' && 'Verifying your email address...'}
            {verificationStatus === 'success' && 'Your email has been verified!'}
            {verificationStatus === 'error' && 'Verification failed'}
          </p>
        </motion.div>

        {/* Verification Status */}
        <motion.div variants={itemVariants}>
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 text-center">
            {verificationStatus === 'verifying' && (
              <div className="flex flex-col items-center justify-center py-6">
                <div className="loading-spinner w-16 h-16 mb-4"></div>
                <p className="text-gray-700 dark:text-gray-300">Please wait while we verify your email...</p>
              </div>
            )}

            {verificationStatus === 'success' && (
              <div className="flex flex-col items-center justify-center py-6">
                <CheckCircle className="w-16 h-16 text-green-500 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Verification Successful!</h3>
                <p className="text-gray-700 dark:text-gray-300 mb-6">Your email has been verified. You can now access all features of Prime Edge.</p>
                <Link to="/login" className="btn btn-primary btn-lg">
                  Sign In
                </Link>
              </div>
            )}

            {verificationStatus === 'error' && (
              <div className="flex flex-col items-center justify-center py-6">
                <XCircle className="w-16 h-16 text-red-500 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Verification Failed</h3>
                <p className="text-gray-700 dark:text-gray-300 mb-6">The verification link is invalid or has expired. Please request a new verification email.</p>
                
                <form onSubmit={resendVerification} className="w-full max-w-sm space-y-4">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 text-left mb-1">
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="input w-full"
                      placeholder="Enter your email address"
                      required
                      pattern={validationRules.email.pattern.value.source}
                      title={validationRules.email.pattern.message}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={isResending}
                    className="btn btn-primary w-full inline-flex items-center justify-center"
                  >
                    {isResending ? (
                      <>
                        <div className="loading-spinner w-5 h-5 mr-2"></div>
                        Sending...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-5 h-5 mr-2" />
                        Resend Verification Email
                      </>
                    )}
                  </button>
                </form>
              </div>
            )}
          </div>
        </motion.div>

        {/* Help Link */}
        <motion.div variants={itemVariants} className="text-center">
          <p className="text-gray-600 dark:text-gray-300">
            Need help?{' '}
            <Link
              to="/contact"
              className="font-medium text-primary-600 hover:text-primary-500 dark:text-primary-400"
            >
              Contact Support
            </Link>
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default EmailVerification;